import React, { FC } from 'react';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { CarouselButton } from '@/components/UI/CarouselButton/CarouselButton';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import FieldGroup from '@/components/UI/Fields/FieldGroup';
import Input from '@/components/UI/Fields/Input';
import { Label } from '@/components/UI/Label/Label';
import Notification from '@/components/UI/Notification/Notification';
import { RadioGroup } from '@/components/UI/RadioGroup/RadioGroup';
import useFormHtml from '@/hooks/useFormHtml';
import { DefectSoortcodeEnum, MaakDefectNietInzetBaarFormType } from '@/types/Defecten';

import useDefectenDialog from './useDefectenDialog';

import styles from '@/components/UI/Dialog/Dialog.module.css';

type AanmakenNietInzetbaarVanafDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  details: WerklijnContextMenuDetails;
};
const MeldDefectDialog: FC<AanmakenNietInzetbaarVanafDialogProps> = ({ showDialog, handleOpenChange, details }) => {
  const { defectState, resetDialog, maakNietInzetbaarFormEvent } = useDefectenDialog(details, handleOpenChange);
  const { formSubmitCallback } = useFormHtml<MaakDefectNietInzetBaarFormType>(maakNietInzetbaarFormEvent);
  return (
    <MwdDialog
      title='Meld defect'
      className={styles.nietMeerInzetbaarVanafDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid={`contextMenu-${details?.treinnummer}-${defectState.defect.eenheidnummer}`}
      hasCloseInHeader={true}
    >
      <Notification style={{ height: '96px', marginBottom: '14px' }}>
        Let op: Meld hier alleen een defect als dat niet lukt in Maximo. Deze moeten hier ook weer worden afgemeld.
      </Notification>
      <form onSubmit={formSubmitCallback}>
        <div>
          <Label>Eenheid</Label>
          <div style={{ color: 'var(--tractie-color-content-default)' }}>{defectState.defect.eenheidnummer}</div>
          <div
            style={{
              height: '2px',
              marginBottom: '8px',
              marginTop: '8px',
              width: '100%',
              background: 'var(--tractie-color-border-subtle)',
            }}
          />
        </div>
        <FieldGroup.Col boxProps={{ width: '100%' }} required fieldId='InzetbaarheidLabel' label='Inzetbaarheid'>
          <RadioGroup
            defaultValue={defectState.defect.soortcode}
            options={[
              { value: DefectSoortcodeEnum.DEFECT_NIET_INZETBAAR, label: 'Niet inzetbaar' },
              { value: DefectSoortcodeEnum.BEPERKING_IN_TREINDIENST, label: 'Beperkt inzetbaar' },
            ]}
            onChange={(newSoort) => defectState.updateSoort(newSoort as DefectSoortcodeEnum)}
          />
        </FieldGroup.Col>
        <div style={{ marginTop: '4px', marginBottom: '4px', gap: '12px', display: 'flex', flexDirection: 'row' }}>
          <FieldGroup.Col boxProps={{ width: '276px' }} required fieldId='startDatumLabel' label='Defect vanaf datum'>
            <CarouselButton
              items={defectState.dateVariations}
              defaultValue={defectState.form.startDatum}
              onChange={(newDate) => defectState.updateDate(newDate)}
            />
          </FieldGroup.Col>
          <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>
            <FieldGroup.Col
              required
              fieldId='startTijd'
              FieldComponent={Input.Time}
              label='Tijd'
              defaultValue={defectState.form.startTijd}
              onChange={(event) => defectState.updateTime(event.currentTarget.value)}
            />
          </div>
        </div>
        <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '14px' }}>
          <FieldGroup.Col
            fieldId='notitieLabel'
            FieldComponent={Input.TextArea}
            label='Notitie'
            data-testid='notitie'
            maxLength={120}
            rows={4}
            defaultValue={defectState.form.toelichting}
            onChange={(event) => defectState.updateToelichting(event.currentTarget.value)}
          />
        </div>
        <MwdDialog.Footer submitCaption='Meld defect' cancelCallback={() => resetDialog(true)} />
      </form>
    </MwdDialog>
  );
};
export default MeldDefectDialog;
